import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiKey, UserSettings } from '../Types';
import { RootState, useAppDispatch, useAppSelector } from '../store';
import { apiKeyService } from '../Services/apiKeyService';
import { setListReloadType } from '../Store/features/environment/environmentSlice';
import { PaginationListType } from '../Types/PaginationParams';
import useModal from '../components/Modal/useModal';
import { getBreakpoint } from '../utils/breakpoint';

const useApiKeys = () => {
    const navigate = useNavigate();
    const userSettings = useAppSelector((state: RootState) => state.userSettings as UserSettings);
    const settings = useAppSelector((state: RootState) => state.settings);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const demo = useAppSelector((state: RootState) => state.environment.demoInstance as boolean);
    const windowWidth = useAppSelector((state: RootState) => state.environment.windowWidth);
    const dispatch = useAppDispatch();
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
    const searchInput = useRef<HTMLInputElement>(null);
    const modal = useModal();

    const handleEdit = useCallback(
        (identifier: string) => {
            navigate(identifier);
        },
        [navigate],
    );

    const deleteApiKey = useCallback(
        (identifier: string) => {
            if (demo) return;

            if (!userSettings.isLoggedIn) return;

            apiKeyService.deleteApiKey(identifier).then(() => {
                dispatch(setListReloadType(PaginationListType.ApiKeys));
            });
        },
        [demo, userSettings.isLoggedIn, dispatch],
    );

    const confirmDeleteApiKey = useCallback(
        async (apiKeyIdentifierToDelete: string) => {
            if (apiKeyIdentifierToDelete) {
                deleteApiKey(apiKeyIdentifierToDelete);
            }
        },
        [deleteApiKey],
    );

    const handleOpenConfirmDialog = useCallback(
        (apiKeyIdentifier: string) => {
            modal.deleteConfirm({
                title: i18n.translate({ id: 'apiKeyDelete.title', sourceName: 'ApiKeys' }),
                content: i18n.translate({ id: 'apiKeyDelete.text', sourceName: 'ApiKeys' }),
                onConfirm: () => confirmDeleteApiKey(apiKeyIdentifier),
            });
        },
        [confirmDeleteApiKey, i18n, modal],
    );

    useEffect(() => {
        if (!userSettings.isLoggedIn) return;
        apiKeyService.getAllApiKeysPaginated().then((response) => {
            setApiKeys(response);
        });
    }, [userSettings.isLoggedIn, demo]);

    const ApiKeysActions = useMemo(
        () => ({
            handleEdit,
            deleteApiKey,
            handleOpenConfirmDialog,
            confirmDeleteApiKey,
        }),
        [handleEdit, deleteApiKey, handleOpenConfirmDialog, confirmDeleteApiKey],
    );

    const handleNavigate = useCallback(
        (path: string) => {
            navigate(path);
        },
        [navigate],
    );

    return {
        apiKeys,
        ApiKeysActions,
        settings,
        demo,
        i18n,
        getBreakpoint,
        windowWidth,
        searchInput,
        handleNavigate,
    };
};

export default useApiKeys;
